import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Mic, KeyRound, Save, Loader2, CheckCircle2, Radio, Plus, Trash2, Compass } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/lib/api';
import { useBrandName } from '@/lib/branding';

export default function VoiceSettingsPage() {
  const APP_NAME = useBrandName();
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [dashboards, setDashboards] = useState([]);

  const load = async () => {
    try {
      const c = await api.voiceAdminConfig();
      setCfg(c);
      setForm({
        feature_enabled: c.feature_enabled !== false,
        wake_phrase: c.wake_phrase || 'Hey INZARA',
        hands_free_default: !!c.hands_free_default,
        realtime_enabled: !!c.realtime_enabled,
        realtime_voice: c.realtime_voice || 'alloy',
        nav_phrases: Array.isArray(c.nav_phrases) ? c.nav_phrases : [],
        openai_api_key: '',
      });
    } catch (e) { toast.error(e.message); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { api.getReports().then((r) => setDashboards(r || [])).catch(() => {}); }, []);

  const PAGES = [
    { label: 'Investigations', path: '/investigations' },
    { label: 'Dashboards', path: '/dashboards' },
    { label: 'Alerts & Sentinels', path: '/alerts' },
    { label: 'KPI Monitor', path: '/kpi-monitor' },
    { label: 'Recommendations', path: '/recommendations' },
    { label: 'Executive Summaries', path: '/executive-summaries' },
    { label: 'Discussions', path: '/discussions' },
    { label: 'Marketplace', path: '/marketplace' },
    { label: 'Semantic Models', path: '/models' },
    { label: 'Home', path: '/home' },
  ];
  const destOptions = [
    ...PAGES.map((p) => ({ ...p, group: 'Pages' })),
    ...dashboards.map((d) => ({ label: d.name, path: `/reports/view?id=${d.report_id}`, group: 'Dashboards' })),
  ];

  const setPhrase = (i, patch) => setForm((f) => {
    const list = [...(f.nav_phrases || [])];
    list[i] = { ...list[i], ...patch };
    return { ...f, nav_phrases: list };
  });
  const addPhrase = () => setForm((f) => ({ ...f, nav_phrases: [...(f.nav_phrases || []), { phrase: '', path: '', label: '' }] }));
  const removePhrase = (i) => setForm((f) => ({ ...f, nav_phrases: (f.nav_phrases || []).filter((_, j) => j !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form };
      if (!payload.openai_api_key) delete payload.openai_api_key; // blank keeps existing
      payload.nav_phrases = (payload.nav_phrases || []).filter((p) => p.phrase?.trim() && p.path?.trim());
      await api.updateVoiceAdminConfig(payload);
      toast.success('Voice settings saved');
      await load();
      setForm((f) => ({ ...f, openai_api_key: '' }));
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!cfg) return <div className="p-8 text-muted-foreground" data-testid="voice-settings-loading">Loading voice settings…</div>;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="voice-settings-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center"><Mic className="w-5 h-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Voice</h1>
          <p className="text-sm text-muted-foreground">Configure hands-free wake behavior and real-time interruptible (Barge-In) voice for AI Explore.</p>
        </div>
      </div>

      {/* Master switch */}
      <Card className="p-5" data-testid="voice-feature-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Voice</h2>
            <p className="text-xs text-muted-foreground">Master switch — turn the voice assistant on or off for AI Explore across this workspace.</p>
          </div>
          <Switch checked={!!form.feature_enabled} onCheckedChange={(v) => set('feature_enabled', v)} data-testid="voice-feature-toggle" />
        </div>
      </Card>

      {/* Hands-free */}
      <Card className="p-5 space-y-4" data-testid="voice-handsfree-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Hands-Free Mode</h2>
            <p className="text-xs text-muted-foreground">Say the wake phrase to start talking; {APP_NAME} auto-listens for follow-ups after each answer.</p>
          </div>
          <Switch checked={!!form.hands_free_default} onCheckedChange={(v) => set('hands_free_default', v)} data-testid="voice-handsfree-toggle" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Wake phrase</Label>
          <Input
            value={form.wake_phrase || ''}
            onChange={(e) => set('wake_phrase', e.target.value)}
            placeholder="Hey INZARA"
            className="h-9 text-sm mt-1 max-w-xs"
            data-testid="voice-wake-phrase"
          />
          <p className="text-[10px] text-muted-foreground/80 mt-1">Customize per brand, e.g. "Hey Cloverleaf".</p>
        </div>
      </Card>

      {/* Barge-In (Realtime) */}
      <Card className="p-5 space-y-4" data-testid="voice-realtime-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><Radio className="w-4 h-4 text-primary" /> Barge-In Voice (OpenAI Realtime)</h2>
            <p className="text-xs text-muted-foreground">Real-time, interruptible speech-to-speech. Requires an OpenAI API key with Realtime access.</p>
          </div>
          <Switch checked={!!form.realtime_enabled} onCheckedChange={(v) => set('realtime_enabled', v)} data-testid="voice-realtime-toggle" />
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground flex items-center gap-1"><KeyRound className="w-3 h-3" /> OpenAI API Key</Label>
          <div className="flex items-center gap-2 mt-1">
            <Input
              type="password"
              value={form.openai_api_key || ''}
              onChange={(e) => set('openai_api_key', e.target.value)}
              placeholder={cfg.api_key_set ? '•••••••• (key set — leave blank to keep)' : 'sk-proj-...'}
              className="h-9 text-sm font-mono max-w-md"
              data-testid="voice-openai-key"
            />
            {cfg.api_key_set && (
              <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px] gap-1" data-testid="voice-key-set-badge">
                <CheckCircle2 className="w-3 h-3" /> Key set ({cfg.api_key_source})
              </Badge>
            )}
          </div>
        </div>
        <div>
          <Label className="text-[11px] text-muted-foreground">Realtime voice</Label>
          <Select value={form.realtime_voice} onValueChange={(v) => set('realtime_voice', v)}>
            <SelectTrigger className="h-9 text-sm mt-1 max-w-xs capitalize" data-testid="voice-realtime-voice"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(cfg.realtime_voices || ['alloy']).map((v) => <SelectItem key={v} value={v} className="text-sm capitalize">{v}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Custom navigation phrases */}
      <Card className="p-5 space-y-3" data-testid="voice-navphrases-card">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold flex items-center gap-2"><Compass className="w-4 h-4 text-primary" /> Custom navigation phrases</h2>
            <p className="text-xs text-muted-foreground">Map a spoken phrase to a page or a specific dashboard, e.g. "show me the executive board".</p>
          </div>
          <Button variant="outline" size="sm" onClick={addPhrase} data-testid="voice-navphrase-add"><Plus className="w-3.5 h-3.5" /> Add</Button>
        </div>
        {(form.nav_phrases || []).length === 0 && <p className="text-[11px] text-muted-foreground/70">No custom phrases yet. Built-in commands like "go to dashboards" still work.</p>}
        <div className="space-y-2">
          {(form.nav_phrases || []).map((row, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`voice-navphrase-row-${i}`}>
              <Input
                value={row.phrase || ''}
                onChange={(e) => setPhrase(i, { phrase: e.target.value })}
                placeholder='say… e.g. "open the loss board"'
                className="h-9 text-sm flex-1"
                data-testid={`voice-navphrase-say-${i}`}
              />
              <span className="text-muted-foreground text-xs">→</span>
              <select
                value={row.path || ''}
                onChange={(e) => { const opt = destOptions.find((o) => o.path === e.target.value); setPhrase(i, { path: e.target.value, label: opt?.label || '' }); }}
                className="h-9 text-sm rounded-lg border border-border/50 bg-background px-2 flex-1 min-w-0"
                data-testid={`voice-navphrase-dest-${i}`}
              >
                <option value="">Choose destination…</option>
                <optgroup label="Pages">
                  {destOptions.filter((o) => o.group === 'Pages').map((o) => <option key={o.path} value={o.path}>{o.label}</option>)}
                </optgroup>
                {destOptions.some((o) => o.group === 'Dashboards') && (
                  <optgroup label="Dashboards">
                    {destOptions.filter((o) => o.group === 'Dashboards').map((o) => <option key={o.path} value={o.path}>{o.label}</option>)}
                  </optgroup>
                )}
              </select>
              <button onClick={() => removePhrase(i)} className="text-muted-foreground hover:text-red-500 p-1" data-testid={`voice-navphrase-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="voice-settings-save">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save settings
        </Button>
      </div>
    </div>
  );
}
